import { Component, OnInit } from '@angular/core';
import {InvoiceListService} from './invoice-list.service';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {InvoiceNew} from '../models/InvoiceNew';
import {lastValueFrom} from 'rxjs';
import {Router} from '@angular/router';
import {CurrencyPipe, DatePipe, CommonModule} from "@angular/common";
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatCardModule, MatIconModule, MatButtonModule, MatChipsModule, RouterModule],
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.css']
})
export class InvoiceListComponent implements OnInit {

  constructor(
      private httpService: InvoiceListService,
      private currencyPipe: CurrencyPipe,
      private datePipe: DatePipe,
      private router: Router,
  ) { }

  public dataSourceInvoices: MatTableDataSource<InvoiceNew> = new MatTableDataSource<InvoiceNew>();
  public invoiceRows: Array<InvoiceNew> = [];
  public readonly displayedColumns: Array<string> = ['referencia', 'descricao', 'vencimento', 'valor', 'status',  'acoes'];

  async ngOnInit(): Promise<any> {
    try {
      this.httpService.showSpinner();
      const invoices = await lastValueFrom(this.httpService.getFaturas());
      const sortedInvoices = [...(invoices as Array<InvoiceNew>)].sort(
        (a, b) => new Date(a.DATA_VENCIMENTO).getTime() - new Date(b.DATA_VENCIMENTO).getTime()
      );

      this.invoiceRows = sortedInvoices;
      this.dataSourceInvoices = new MatTableDataSource<InvoiceNew>(sortedInvoices);
    } catch (e){
      console.error(e);
      this.httpService.toasterError('Houve um erro ao montar esta tela');
    } finally {
      this.httpService.hideSpinner();
    }
  }

  public openInvoice(id: number): void {
    this.router.navigate(['principal/faturas/' + id]);
  }

  public formatDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '—';
    }

    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? '—';
  }

  public formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return this.currencyPipe.transform(value, 'BRL', 'symbol', '1.2-2', 'pt-BR') ?? '—';
  }

  public statusLabel(invoice: InvoiceNew): string {
    if (invoice.DATA_PAGAMENTO || invoice.CODIGO_STATUS === 4) {
      return 'Pago';
    }

    if (invoice.INADIMPLENTE) {
      return 'Vencido';
    }

    return 'Aguardando pagamento';
  }

  public statusClass(invoice: InvoiceNew): string {
    if (invoice.DATA_PAGAMENTO) {
      return 'bg-success';
    }

    if (invoice.INADIMPLENTE) {
      return 'bg-danger';
    }

    return 'bg-info';
  }

  public paymentNote(invoice: InvoiceNew): string {
    if (!invoice.DATA_PAGAMENTO) {
      return '';
    }

    return `Pago em ${this.formatDate(invoice.DATA_PAGAMENTO)}`;
  }

  public invoiceReference(invoice: InvoiceNew): string {
    if (invoice.MES_REFERENCIA && invoice.ANO_REFERENCIA) {
      return `${invoice.MES_REFERENCIA}/${invoice.ANO_REFERENCIA}`;
    }

    return invoice.MES_ANO || '—';
  }

  public dueHint(invoice: InvoiceNew): string {
    if (invoice.DATA_PAGAMENTO || invoice.CODIGO_STATUS === 4) {
      return '';
    }

    const diff = this.daysUntilDue(invoice);

    if (diff === 0) {
      return 'Vence hoje';
    }

    if (diff > 0) {
      return diff === 1 ? 'Vence em 1 dia' : `Vence em ${diff} dias`;
    }

    const overdueDays = Math.abs(diff);
    return overdueDays === 1 ? '1 dia em atraso' : `${overdueDays} dias em atraso`;
  }

  public dueHintClass(invoice: InvoiceNew): string {
    if (invoice.DATA_PAGAMENTO || invoice.CODIGO_STATUS === 4) {
      return 'due-future';
    }

    const diff = this.daysUntilDue(invoice);
    if (diff < 0) {
      return 'due-overdue';
    }
    if (diff === 0) {
      return 'due-today';
    }
    if (diff <= 5) {
      return 'due-soon';
    }
    return 'due-future';
  }

  public isNextPendingInvoice(invoice: InvoiceNew): boolean {
    const nextPending = this.invoiceRows
      .filter(item => !item.DATA_PAGAMENTO && item.CODIGO_STATUS !== 4)
      .sort((a, b) => new Date(a.DATA_VENCIMENTO).getTime() - new Date(b.DATA_VENCIMENTO).getTime())[0];

    return nextPending?.CODIGO_FATURA === invoice.CODIGO_FATURA;
  }

  private daysUntilDue(invoice: InvoiceNew): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(invoice.DATA_VENCIMENTO);
    dueDate.setHours(0, 0, 0, 0);

    return Math.round((dueDate.getTime() - today.getTime()) / 86400000);
  }
}
